import { ReactNode } from "react";
import { SvgIcon, SvgIconProps } from "@mui/material";

/**
 * The stroke glyphs of the Compare versions frames, drawn inline so they take `currentColor` (the Figma SVGs bake
 * in the mock's colours, and the workspace rule is one colour from the surrounding text). All are decorative: the
 * text beside them names the control.
 *
 * `fill: none` is set through `sx` because MUI's SvgIcon puts `fill: currentColor` in its CSS, which beats the
 * attribute and would turn every outline into a solid blob.
 */
const make = (children: ReactNode, strokeWidth = 2) => {
  const Glyph = (props: SvgIconProps) => {
    const { sx, ...rest } = props;
    return (
      <SvgIcon
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        focusable={false}
        {...rest}
        sx={[{ fill: "none" }, ...(Array.isArray(sx) ? sx : [sx ?? {}])]}
      >
        {children}
      </SvgIcon>
    );
  };
  return Glyph;
};

export const SearchGlyph = make(
  <>
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3.6-3.6" />
  </>,
);

/** Two columns: the "compare" mark of the page, the nav item and the buttons. */
export const CompareGlyph = make(
  <>
    <rect x="3" y="4" width="7" height="16" rx="1.5" />
    <rect x="14" y="4" width="7" height="16" rx="1.5" />
    <path d="M5.5 9h2M5.5 13h2M16.5 9h2M16.5 13h2" />
  </>,
);

export const UploadGlyph = make(<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />);

export const FileGlyph = make(<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6" />);

export const FileTextGlyph = make(
  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13H8M16 17H8M10 9H8" />,
);

/** A card: "From my contracts". */
export const CardGlyph = make(
  <>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </>,
);

export const InfoGlyph = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-4M12 8h.01" />
  </>,
);

export const LockGlyph = make(
  <>
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </>,
);

export const ArrowLeftGlyph = make(<path d="M19 12H5M12 19l-7-7 7-7" />);
export const ArrowRightGlyph = make(<path d="M5 12h14M12 5l7 7-7 7" />);
export const CheckGlyph = make(<path d="M20 6L9 17l-5-5" />, 2.4);
export const CloseGlyph = make(<path d="M18 6L6 18M6 6l12 12" />);
export const ChevronDownGlyph = make(<path d="M6 9l6 6 6-6" />);
export const PlusGlyph = make(<path d="M12 5v14M5 12h14" />);
export const MinusGlyph = make(<path d="M5 12h14" />);
export const CodeGlyph = make(<path d="M16 18l6-6-6-6M8 6l-6 6 6 6" />);
export const ListGlyph = make(<path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />);
export const PilcrowGlyph = make(<path d="M13 4v16M17 4v16M19 4H9.5a4.5 4.5 0 0 0 0 9H13" />);
export const TableGlyph = make(
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18M3 15h18M9 3v18" />
  </>,
);

export const RefreshGlyph = make(
  <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8M3 3v5h5M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16M16 16h5v5" />,
);

export const AlertTriangleGlyph = make(
  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" />,
);

export const AlertCircleGlyph = make(
  <>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v4M12 16h.01" />
  </>,
);

export const HourglassGlyph = make(
  <path d="M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22M7 2v4.172a2 2 0 0 0 .586 1.414L12 12l4.414-4.414A2 2 0 0 0 17 6.172V2" />,
);

export const ImageGlyph = make(
  <>
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="9" cy="9" r="2" />
    <path d="M21 15l-5-5L5 21" />
  </>,
);

export const ScanGlyph = make(<path d="M3 7V5a2 2 0 0 1 2-2h2M17 3h2a2 2 0 0 1 2 2v2M21 17v2a2 2 0 0 1-2 2h-2M7 21H5a2 2 0 0 1-2-2v-2M7 12h10" />);

export const PencilGlyph = make(<path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />);

/** Arrows going both ways: a moved clause. */
export const SwapGlyph = make(<path d="M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3" />);

/** A line under a gap: a blank that was filled. */
export const BlankGlyph = make(<path d="M6 3v7a6 6 0 0 0 12 0V3M4 21h16" />);
